import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, PaginationQueryWithFields, Version, Versions } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { Manufacturer, ManufacturerVersion, UpsertManufacturer } from "./types";

export const useManufacturersApi = () => {
  const api = useApi();

  const useAdminManufacturers = useCallback(
    <K extends keyof Manufacturer>(
      query: PaginationQueryWithFields<Pick<Manufacturer, K>>,
      signal: AbortSignal,
      hasPermission?: boolean,
    ) => {
      const url = buildUrl<Pick<Manufacturer, K>>("/admin/manufacturers", query);

      const key = hasPermission === false ? false : url;

      return useSWR<Paginated<Pick<Manufacturer, K>> | null, CustomError, string | null>(
        key as string | null,
        (url: string) => api.get<Paginated<Pick<Manufacturer, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminManufacturer = useCallback(
    (manufacturerId: string, signal: AbortSignal) => {
      const url = `/admin/manufacturers/${manufacturerId}`;
      return useSWRMutation<Manufacturer | null, CustomError, string>(url, (url) =>
        api.get<Manufacturer>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminManufacturer = useCallback(
    async (body: UpsertManufacturer): Promise<{ id: string }> => {
      const url = `/admin/manufacturers`;
      const id = await api.post<UpsertManufacturer, { id: string }>(url, body);
      api.mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminManufacturer = useCallback(
    async (id: string, body: UpsertManufacturer): Promise<void> => {
      const url = `/admin/manufacturers/${id}`;
      await api.put<UpsertManufacturer>(url, body);
      api.mutateCache([`/admin/manufacturers`, `/admin/manufacturers/${id}/versions`]);
    },
    [api],
  );

  const deleteAdminManufacturer = useCallback(
    async (manufacturerId: string): Promise<void> => {
      const url = `/admin/manufacturers/${manufacturerId}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/manufacturers`]);
      });
    },
    [api],
  );

  const getAdminManufacturerHistory = useCallback(
    (query: PaginationQuery, manufacturerId: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/manufacturers/${manufacturerId}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminManufacturerVersion = useCallback(
    (manufacturerId: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/manufacturers/${manufacturerId}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<ManufacturerVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<ManufacturerVersion>>({ url, signal }),
      );
    },
    [api],
  );

  return {
    updateAdminManufacturer,
    insertAdminManufacturer,
    useAdminManufacturers,
    deleteAdminManufacturer,
    getAdminManufacturerHistory,
    getAdminManufacturerVersion,
    getAdminManufacturer,
  };
};
